import React, { Dispatch, SetStateAction, useContext, useState } from 'react';
import { To } from 'react-router-dom';
import {
	ButtonState,
	Countries,
	CustomerService,
	getPreauthOfferHash,
	getUrlRedirectPath,
	ICustomer,
	IPreauthorizationResponse,
	ISepaInfo,
	PaymentMethods,
	PayOneService,
	Variants,
	IAdditionalInformation,
	OfferContext,
	setRouteLangParam,
	ITypeSepaMandatFields,
	onRedirect,
	ITypeCreditCardDataFields,
} from '@adac-mp/shared';
import { Button, PaymentModal, SepaDrawer } from '../../index';
type PaymentContentfulLabels = { creditCard: ITypeCreditCardDataFields; sepa?: ITypeSepaMandatFields };

interface ICheckoutButtonProps {
	variant: Variants;
	label?: string;
	paymentUpdate?: boolean;
	paymentLabels?: PaymentContentfulLabels;
	countryUrl?: Countries;
	paymentMethod: PaymentMethods;
	paymentLabel: string;
	customer: ICustomer | undefined;
	buttonState: ButtonState;
	additionalData?: IAdditionalInformation;
	sepaData?: ISepaInfo;
	className?: string;
	onClick?: () => void;
	setError?: Dispatch<SetStateAction<string | undefined>>;
	updateButtonState: (buttonState: ButtonState) => void;
	onOrderCompleted: (pathname: To) => void;
}

export const CheckoutButton = ({
	paymentMethod,
	paymentLabel,
	label = 'Zahlungspflichtig bestellen',
	paymentUpdate = false,
	paymentLabels = undefined,
	variant,
	buttonState,
	customer,
	additionalData,
	sepaData,
	countryUrl,
	className,
	setError = () => {},
	updateButtonState,
	onOrderCompleted,
	onClick = () => {},
}: ICheckoutButtonProps) => {
	const isDkvPage = variant === 'dkv';
	const { lang } = useContext(OfferContext);
	const [showPayment, setShowPayment] = useState<boolean>(false);
	const [showSepaModal, setShowSepaModal] = useState<boolean>(false);
	const [showExistingSepaModal, setShowExistingSepaModal] = useState<boolean>(false);
	const [sepaText, setSepaText] = useState<string>('');
	const [mandate, setMandate] = useState<string | undefined>(undefined);

	const payPalRequest = () => {
		const customerHash = sessionStorage.getItem('customerHash');
		const cartHash = sessionStorage.getItem('cartHash');
		const token = sessionStorage.getItem('cartToken') || sessionStorage.getItem('customerToken');
		const offerHash = getPreauthOfferHash(isDkvPage);
		const domain = getUrlRedirectPath();
		const isPreviewEnvironment = process.env.VERCEL_ENV !== 'production';

		if (customerHash) {
			const body = {
				customerHash,
				cartHash,
				offerHash,
				successUrl: `${domain}/processing/${variant}${setRouteLangParam(lang)}${
					isPreviewEnvironment ? '?featureAppPreview=true' : ''
				}`,
				errorUrl: `${domain}/error${setRouteLangParam(lang)}${
					isPreviewEnvironment ? '?featureAppPreview=true' : ''
				}`,
				backUrl: `${domain}/${isDkvPage ? 'camper-mautbox' : `${countryUrl}`}/checkout${setRouteLangParam(
					lang,
				)}${isPreviewEnvironment ? '?featureAppPreview=true' : ''}`,
			};
			PayOneService.payPalPreauthorization(body, token, lang).then((res: IPreauthorizationResponse) => {
				if (res.status !== 'ERROR') {
					setError(undefined);
					if (res.offerHash) {
						sessionStorage.setItem('offerHash', res.offerHash);
					}
					if (additionalData) {
						sessionStorage.setItem('orderDetail', JSON.stringify(additionalData));
					}
					if (res.redirectUrl) {
						onRedirect(res.redirectUrl);
					}
				} else {
					setError(res.payoneError?.CustomerMessage);
				}
				updateButtonState('active');
			});
		}
	};

	const paypalAmendRequest = () => {
		const customerHash = sessionStorage.getItem('customerHash');
		const orderHash = sessionStorage.getItem('orderHash');
		const token = sessionStorage.getItem('token');
		const domain = getUrlRedirectPath();
		const isPreviewEnvironment = process.env.VERCEL_ENV !== 'production';

		if (customerHash && orderHash) {
			const body = {
				customerHash,
				orderHash,
				successUrl: `${domain}/processing/updatepayment${setRouteLangParam(lang)}${
					isPreviewEnvironment ? '?featureAppPreview=true' : ''
				}`,
				errorUrl: `${domain}/error${setRouteLangParam(lang)}${
					isPreviewEnvironment ? '?featureAppPreview=true' : ''
				}`,
				backUrl: `${domain}/processing/updatepayment${setRouteLangParam(lang)}${
					isPreviewEnvironment ? '?featureAppPreview=true' : ''
				}`,
			};
			PayOneService.payPalAmendPreauthorization(body, token, lang).then((res: IPreauthorizationResponse) => {
				if (res.status !== 'ERROR') {
					setError(undefined);
					if (res.offerHash) {
						sessionStorage.setItem('offerHash', res.offerHash);
					}
					if (res.redirectUrl) {
						onRedirect(res.redirectUrl);
					}
				} else {
					setError(res.payoneError?.CustomerMessage);
				}
				updateButtonState('active');
			});
		}
	};

	const handleSepaMandate = (res) => {
		updateButtonState('active');
		if (res.code === 200) {
			if (res.offerHash) {
				sessionStorage.setItem('offerHash', res.offerHash);
			}
			setMandate(res.mandate);
			setError(undefined);
			if (res.htmlText) {
				const text = decodeURIComponent(res.htmlText)
					.replace(/\+/g, ' ')
					.replace(
						'Name des Zahlungspflichtigen:',
						paymentLabels?.sepa?.cardholder || 'Name des Zahlungspflichtigen (Kontoinhaber):',
					);
				setSepaText(text);
				setShowSepaModal(true);
			} else if (res.mandateStatus === 'active') {
				setShowExistingSepaModal(true);
			}
		} else {
			setError(res.errors[0].customerMessage);
		}
	};

	const sepaRequest = () => {
		const customerHash = sessionStorage.getItem('customerHash');
		const token = sessionStorage.getItem('cartToken') || sessionStorage.getItem('customerToken');
		const offerHash = getPreauthOfferHash(isDkvPage);
		const cartHash = sessionStorage.getItem('cartHash');
		if (customerHash && (offerHash || cartHash) && sepaData) {
			PayOneService.manageMandate({ customerHash, offerHash, cartHash, ...sepaData }, token, lang).then(
				handleSepaMandate,
			);
		}
	};
	const sepaAmendRequest = () => {
		const customerHash = sessionStorage.getItem('customerHash');
		const token = sessionStorage.getItem('token');
		const orderHash = sessionStorage.getItem('orderHash');
		if (customerHash && orderHash && sepaData) {
			PayOneService.amendManageMandate({ customerHash, orderHash, ...sepaData }, token, lang).then(
				handleSepaMandate,
			);
		}
	};

	const checkPaymentMethod = () => {
		sessionStorage.setItem('paymentMethod', paymentLabel);
		if (paymentMethod === '2') {
			updateButtonState('active');
			setShowPayment(true);
		}
		if (paymentMethod === '1') {
			return paymentUpdate ? paypalAmendRequest() : payPalRequest();
		}
		if (paymentMethod === '3') {
			return paymentUpdate ? sepaAmendRequest() : sepaRequest();
		}
	};

	const createNewCustomer = (newCustomer: ICustomer) => {
		CustomerService.create({ customer: newCustomer }).then((res) => {
			if (res.code === 200) {
				sessionStorage.setItem('customerHash', res.customerHash);
				sessionStorage.setItem('customerToken', res.token);
				checkPaymentMethod();
			}
		});
	};

	const updateCustomer = (updatedCustomer: ICustomer, customerHash: string, customerToken: string) => {
		const updateRequest = {
			customer: {
				customerHash,
				...updatedCustomer,
			},
		};
		CustomerService.update(updateRequest, customerToken).then((res) => {
			if (res.code === 200) {
				checkPaymentMethod();
			}
		});
	};

	const onOrderClick = (ev) => {
		ev.preventDefault();
		if (buttonState === 'disabled') {
			onClick();
			return;
		}
		const customerHash = sessionStorage.getItem('customerHash');
		const customerToken = sessionStorage.getItem('customerToken');
		if (paymentUpdate) {
			checkPaymentMethod();
		}
		if (customer && buttonState === 'active') {
			if (!customerToken || !customerHash) {
				createNewCustomer(customer);
			} else if (customer?.person?.memberNo) {
				// workaround as update function doesn't check for member number
				CustomerService.checkMemberNo({ memberNo: customer.person?.memberNo }, lang).then(({ code }) => {
					if (code === 200) {
						updateCustomer(customer, customerHash, customerToken);
					}
				});
			} else {
				updateCustomer(customer, customerHash, customerToken);
			}
		}
		updateButtonState('loading');
	};

	return (
		<>
			<Button
				fullWidth
				size="lg"
				data-cy="Button: bestellen"
				type="submit"
				loading={buttonState === 'loading'}
				className={`mt-5 ${className}`}
				tracking={{
					funnel_cta: {
						label: 'Zahlungspflichtig bestellen',
						element: 'Checkout',
					},
				}}
				onClick={onOrderClick}>
				{label}
			</Button>
			<PaymentModal
				labels={paymentLabels?.creditCard}
				paymentUpdate={paymentUpdate}
				countryUrl={countryUrl}
				customer={customer}
				showModal={showPayment}
				variant={variant}
				additionalData={additionalData}
				onClose={setShowPayment}
				onRedirect={onRedirect}
				onOrderCompleted={onOrderCompleted}
			/>
			{sepaData && mandate && (
				<SepaDrawer
					paymentUpdate={paymentUpdate}
					currentLabels={paymentLabels?.sepa}
					variant={variant}
					isOpen={showSepaModal}
					showExistingSepaModal={showExistingSepaModal}
					sepaText={sepaText}
					iban={sepaData}
					mandate={mandate}
					additionalData={additionalData}
					setShowExistingSepaModal={setShowExistingSepaModal}
					setIsOpen={setShowSepaModal}
					onOrderCompleted={onOrderCompleted}
				/>
			)}
		</>
	);
};
